import { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { useAuth } from "@/context/AuthContext";
import { Handshake, Coins, TreeDeciduous, Sun, Users, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";

const MODELS = [
  {
    icon: Handshake,
    title: "Lease Model",
    body: "Long-tenure land lease (25+ years) with annual escalation and security deposit.",
  },
  {
    icon: Users,
    title: "Joint Development",
    body: "Co-development structures with equity participation for qualified landowners.",
  },
  {
    icon: Coins,
    title: "Revenue Sharing",
    body: "Performance-linked revenue share on generation, with floor & ceiling guarantees.",
  },
  {
    icon: Sun,
    title: "PM-KUSUM Opportunities",
    body: "Component-A linkage for landowners near 33/11 kV substations.",
  },
  {
    icon: TreeDeciduous,
    title: "Joint Cultivation",
    body: "Agri-PV models allowing limited cropping alongside elevated solar arrays.",
  },
  {
    icon: MapPin,
    title: "Land Aggregation",
    body: "We aggregate contiguous parcels into bankable >25 acre clusters.",
  },
];

const REQUIREMENTS = [
  "Minimum 25 acres contiguous (or aggregatable) parcel",
  "Land within 10 km of 33 kV / 132 kV / 220 kV substation",
  "Clear title with revenue records (Pahani / 7/12 / RoR)",
  "Non-agri / agri (with conversion potential)",
  "Slope under 5% and free from forest / waterbody encumbrance",
];

export default function Landowner() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    fullName: user?.name || "",
    phone: "",
    email: user?.email || "",
    state: "",
    district: "",
    acres: "",
    distanceKm: "",
    titleStatus: "Clear Title",
    model: "Lease Model",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.fullName || !form.phone || !form.acres) {
      toast.error("Please complete name, phone and land area fields.");
      return;
    }
    if (user) {
      try {
        const all = JSON.parse(localStorage.getItem("vpcl_enquiries") || "[]");
        all.unshift({
          id: crypto.randomUUID(),
          userId: user.id,
          type: "Landowner",
          subject: `Land offer — ${form.acres} acres, ${form.district || form.state || "India"}`,
          summary: `Model: ${form.model}. Title: ${form.titleStatus}. ${form.notes}`.trim().slice(0, 200),
          createdAt: new Date().toISOString(),
        });
        localStorage.setItem("vpcl_enquiries", JSON.stringify(all));
      } catch { /* ignore */ }
    }
    toast.success("Enquiry received. Our landowner desk will contact you within 48 hours.");
    setSubmitted(true);
  };

  return (
    <div data-testid="landowner-page">
      <PageHero
        overline="Landowner Opportunities"
        title="Land that powers India's future."
        subtitle="Unlock long-term, predictable revenue from your land through lease, joint development, revenue sharing or PM-KUSUM linkage with VPCL Infra."
        image="https://images.pexels.com/photos/35501726/pexels-photo-35501726.jpeg?auto=compress&cs=tinysrgb&w=2000"
        testid="landowner-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp">
          <div className="overline mb-4">Partnership Models</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-14 max-w-2xl leading-tight">
            Six ways to monetise your land.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MODELS.map((m, i) => (
              <div key={m.title} data-testid={`model-${i}`} className="card-corp">
                <m.icon className="text-[#16A34A] mb-5" size={32} strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-semibold text-[#0B2341] mb-3">{m.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#F8F9FA]">
        <div className="container-corp grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">What We Look For</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-6 leading-tight">
              Eligibility criteria.
            </h2>
            <p className="text-[#4A5568] leading-relaxed mb-8">
              We engage with landowners offering bankable parcels suitable for utility-scale or distribution-tied solar plants. Even if a single criterion is not met, share your details — our team can advise on suitability.
            </p>
            <img
              src="https://images.unsplash.com/photo-1629726797843-618688139f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Solar park aerial view"
              className="w-full h-72 object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {REQUIREMENTS.map((r, i) => (
                <li key={i} data-testid={`req-${i}`} className="flex items-start gap-4 p-5 bg-white border border-[#E2E8F0]">
                  <CheckCircle2 className="text-[#16A34A] mt-0.5 flex-shrink-0" size={20} />
                  <span className="text-[#0A1118]">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="section-padding bg-[#0B2341] text-white" data-testid="landowner-form-section">
        <div className="container-corp grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Landowner Enquiry</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold leading-tight mb-6">
              Submit your land details.
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Our landowner desk reviews every submission and reverts within 48 hours with a preliminary indication and next steps.
            </p>
            <div className="space-y-3 text-sm text-white/75">
              <div className="flex items-center gap-3"><ArrowRight size={14} className="text-[#16A34A]" /> 48-hour response time</div>
              <div className="flex items-center gap-3"><ArrowRight size={14} className="text-[#16A34A]" /> Strict confidentiality of submissions</div>
              <div className="flex items-center gap-3"><ArrowRight size={14} className="text-[#16A34A]" /> Free site evaluation for shortlisted parcels</div>
            </div>
          </div>
          <div className="lg:col-span-7 bg-white text-[#0B2341] p-8 md:p-10">
            {submitted ? (
              <div data-testid="landowner-success" className="text-center py-12">
                <CheckCircle2 className="text-[#16A34A] mx-auto mb-6" size={64} strokeWidth={1.25} />
                <h3 className="font-heading text-2xl font-semibold mb-3">Enquiry Received</h3>
                <p className="text-[#4A5568] max-w-md mx-auto">
                  Thank you, {form.fullName}. Our landowner desk will reach you on{" "}
                  <span className="font-mono">{form.phone}</span> within 48 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name *" testid="landowner-name">
                    <input required value={form.fullName} onChange={update("fullName")} className={inputCls} data-testid="input-name" />
                  </Field>
                  <Field label="Phone *" testid="landowner-phone">
                    <input required type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="input-phone" />
                  </Field>
                </div>
                <Field label="Email">
                  <input type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="input-email" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="State">
                    <input value={form.state} onChange={update("state")} className={inputCls} data-testid="input-state" />
                  </Field>
                  <Field label="District">
                    <input value={form.district} onChange={update("district")} className={inputCls} data-testid="input-district" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Land Area (acres) *">
                    <input required type="number" value={form.acres} onChange={update("acres")} className={inputCls} data-testid="input-acres" />
                  </Field>
                  <Field label="Distance from Substation (km)">
                    <input type="number" value={form.distanceKm} onChange={update("distanceKm")} className={inputCls} data-testid="input-distance" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Title Status">
                    <select value={form.titleStatus} onChange={update("titleStatus")} className={inputCls} data-testid="select-title">
                      <option>Clear Title</option>
                      <option>Joint Patta</option>
                      <option>In Conversion</option>
                      <option>Under Litigation</option>
                    </select>
                  </Field>
                  <Field label="Preferred Model">
                    <select value={form.model} onChange={update("model")} className={inputCls} data-testid="select-model">
                      <option>Lease Model</option>
                      <option>Joint Development</option>
                      <option>Revenue Sharing</option>
                      <option>PM-KUSUM</option>
                      <option>Open / Need Advice</option>
                    </select>
                  </Field>
                </div>
                <Field label="Additional Notes">
                  <textarea rows={4} value={form.notes} onChange={update("notes")} className={inputCls} data-testid="input-notes" />
                </Field>
                <button type="submit" className="btn-accent w-full" data-testid="landowner-submit">
                  Submit Enquiry
                  <ArrowRight size={16} className="ml-2" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#E2E8F0] focus:border-[#0B2341] focus:ring-1 focus:ring-[#0B2341] focus:outline-none rounded-none px-4 py-3 text-sm text-[#0A1118] placeholder:text-[#4A5568]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#0B2341] uppercase tracking-[0.14em] mb-2">{label}</span>
      {children}
    </label>
  );
}
